import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { AppHeader } from '../components/AppHeader';
import { ActivityCalendar } from '../components/ActivityCalendar';
import { TierBadge } from '../components/TierBadge';
import './ProfilePage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Stats {
  games_played: number;
  games_won: number;
  total_time: number;
  best_time: number | null;
  total_mistakes: number;
  win_rate: number;
  rating?: number;
  by_difficulty: Record<string, { played: number; won: number; best_time: number | null; avg_time: number | null }>;
}

interface MpPlayerResult {
  nickname: string;
  place: number;
  score: number;
  mistakes: number;
  elapsed: number;
  is_finished: boolean;
}

interface GameHistory {
  id: string;
  difficulty: number;
  mistakes: number;
  hints_used: number;
  elapsed_time: number;
  is_finished: boolean;
  is_multiplayer: boolean;
  room_name: string | null;
  mp_results: MpPlayerResult[] | null;
  created_at: string | null;
}

export function PublicProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [nickname, setNickname] = useState('');
  const [memberSince, setMemberSince] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [history, setHistory] = useState<GameHistory[]>([]);
  const [activity, setActivity] = useState<Record<string, { solo: number; multiplayer: number; daily: number }>>({});
  const [tab, setTab] = useState<'stats' | 'history'>('stats');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/stats/user/${userId}`)
      .then(r => {
        if (!r.ok) { setNotFound(true); setLoading(false); return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setNickname(data.nickname);
        setMemberSince(data.created_at ? new Date(data.created_at).toLocaleDateString() : '');
        setStats(data.stats);
        setHistory(data.games ?? []);
        setActivity(data.activity ?? {});
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [userId]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) return <div className="profile-page"><p className="empty-state">{t.common.loading}</p></div>;
  if (notFound) return (
    <div className="profile-page">
      <AppHeader onBack={() => navigate(-1 as any)} backLabel={t.common.back} title={t.nav.profile} />
      <p className="empty-state">{t.profile.userNotFound}</p>
    </div>
  );

  return (
    <div className="profile-page">
      <SEO path="/user" title={`${nickname} — Sudoku Area`} />
      <AppHeader onBack={() => navigate(-1 as any)} backLabel={t.common.back} />
      <div className="public-profile-header">
        <span className="public-profile-avatar">{nickname[0]?.toUpperCase()}</span>
        <div className="public-profile-info">
          <span className="public-profile-name">{nickname}</span>
          {stats?.rating != null && stats.rating > 0 && (
            <div style={{ marginTop: 4 }}>
              <TierBadge rating={stats.rating} size="md" />
            </div>
          )}
          {memberSince && <span className="public-profile-since">{t.profile.memberSince.replace('{date}', memberSince)}</span>}
        </div>
      </div>

      <div className="profile-tabs">
        <button className={`tab-btn ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>
          {t.profile.stats}
        </button>
        <button className={`tab-btn ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          {t.profile.history}
        </button>
      </div>

      {tab === 'stats' && stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.games_played}</div>
            <div className="stat-label">{t.profile.gamesPlayed}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.games_won}</div>
            <div className="stat-label">{t.profile.gamesWon}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">%{stats.win_rate}</div>
            <div className="stat-label">{t.profile.winRate}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.best_time ? formatTime(stats.best_time) : '—'}</div>
            <div className="stat-label">{t.profile.bestTime}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total_mistakes}</div>
            <div className="stat-label">{t.profile.totalMistakes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total_time ? formatTime(stats.total_time) : '—'}</div>
            <div className="stat-label">{t.profile.totalTime}</div>
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <ActivityCalendar activity={activity} />
      )}

      {tab === 'stats' && stats && (() => {
        const diffEntries = [1, 2, 3, 4, 5]
          .map(d => ({ d, data: stats.by_difficulty[String(d)] }))
          .filter(({ data }) => data && data.played > 0);
        if (diffEntries.length === 0) return null;
        return (
          <div className="insights-section">
            <h3 className="insights-title">{t.profile.insightsTitle}</h3>
            <div className="diff-analysis-grid">
              {diffEntries.map(({ d, data }) => (
                <div key={d} className="diff-analysis-card">
                  <div className="diff-analysis-name">{t.difficulties[d]}</div>
                  <div className="diff-analysis-rows">
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.gamesLabel}</span>
                      <span className="da-value">{data.played}</span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.winRateLabel}</span>
                      <span className="da-value">{data.played > 0 ? `%${Math.round(data.won / data.played * 100)}` : '—'}</span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.avgTimeLabel}</span>
                      <span className="da-value">{data.avg_time ? formatTime(data.avg_time) : '—'}</span>
                    </div>
                    <div className="diff-analysis-row">
                      <span className="da-label">{t.profile.bestTimeLabel}</span>
                      <span className="da-value">{data.best_time ? formatTime(data.best_time) : '—'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {tab === 'stats' && !stats && <p className="empty-state">{t.profile.noStats}</p>}

      {tab === 'history' && (
        <div className="history-list">
          {history.length === 0 ? (
            <p className="empty-state">{t.profile.noHistory}</p>
          ) : (
            history.map(game => (
              <div key={game.id} className="history-item-wrap">
                <div className="history-item">
                  <div className="history-diff">
                    {t.difficulties[game.difficulty]}
                    {game.is_multiplayer && <span className="history-mp-badge">MP</span>}
                  </div>
                  <div className="history-details">
                    <span>{formatTime(game.elapsed_time)}</span>
                    <span>{game.mistakes} {t.common.mistakes.toLowerCase()}</span>
                    <span>{game.hints_used} {t.common.hints.toLowerCase()}</span>
                    {!game.is_finished && <span className="history-failed">{t.profile.lost}</span>}
                  </div>
                  <div className="history-date">
                    {game.room_name && <span className="history-room-name">{game.room_name}</span>}
                    {game.created_at ? new Date(game.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
                {game.mp_results && game.mp_results.length > 0 && (
                  <div className="history-mp-detail">
                    {game.mp_results.map((p, i) => (
                      <div key={i} className="mp-result-row">
                        <span className="mp-result-place">#{p.place}</span>
                        <span className="mp-result-name">{p.nickname}</span>
                        <span className="mp-result-score">{t.profile.pointsValue.replace('{n}', String(p.score))}</span>
                        <span className="mp-result-stats">{t.profile.mistakesTime.replace('{mistakes}', String(p.mistakes)).replace('{time}', formatTime(p.elapsed))}</span>
                        {!p.is_finished && <span className="history-failed">{t.profile.lost}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
